import { useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, Sparkles, LayoutTemplate, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared GSAP entrance animation helper — appended to each template section.
// Targets the section itself with a fade-up entrance on scroll.
// GSAP + ScrollTrigger are already loaded in every Orbis site.
const ANIM_SCRIPT = (id) => `<script>
(function(){
  if(typeof gsap==='undefined'||typeof ScrollTrigger==='undefined') return;
  gsap.registerPlugin(ScrollTrigger);
  var el=document.getElementById('${id}');
  if(!el) return;
  gsap.fromTo(el,{opacity:0,y:32},{opacity:1,y:0,duration:0.8,ease:'power2.out',scrollTrigger:{trigger:el,start:'top 88%',once:true}});
})();
<\/script>`;

// Generates a stable unique-enough ID for each template insertion
const sectionId = (type) => `orbis-${type}-${Math.random().toString(36).slice(2,7)}`;

// Reusable 5-star SVG (compact)
const STARS = `<div class="flex gap-0.5 mb-4">${Array(5).fill('<svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>').join('')}</div>`;

// Reusable checkmark SVG
const CHECK = `<svg class="w-4 h-4 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`;

// ─── Section Templates ────────────────────────────────────────────────────────
// preview: [from-color, to-color] for the gradient swatch on the card

const buildTemplate = (type, label, description, category, preview, html) => {
  const id = sectionId(type);
  const animated = `${html.replace(/<section /, `<section id="${id}" `)}\n${ANIM_SCRIPT(id)}`;
  return { type, label, description, category, preview, html: animated };
};

const SECTION_TEMPLATES = [
  // ── Landing ──────────────────────────────────────────────────────────────
  buildTemplate('hero', 'Hero', 'Full-width hero with headline, subtext, and dual CTAs', 'Landing',
    ['from-slate-900', 'to-blue-950'],
    `<section class="py-24 px-6 text-center bg-gradient-to-br from-slate-900 to-slate-800">
  <div class="max-w-4xl mx-auto">
    <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-slate-300 text-sm mb-8">
      <span class="w-1.5 h-1.5 rounded-full bg-green-400"></span> New — just launched
    </div>
    <h1 class="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">Your Compelling<br>Headline Here</h1>
    <p class="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">A clear description of your value proposition that resonates with your target audience and drives action.</p>
    <div class="flex gap-4 justify-center flex-wrap">
      <a href="#" class="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-blue-600/30 hover:-translate-y-0.5">Get Started Free</a>
      <a href="#" class="px-8 py-4 border border-slate-600 hover:border-slate-400 text-slate-300 hover:text-white font-semibold rounded-xl transition-colors flex items-center gap-2">Watch Demo <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></a>
    </div>
    <p class="text-slate-500 text-sm mt-6">No credit card required · Free 14-day trial</p>
  </div>
</section>`),

  buildTemplate('hero_split', 'Hero Split', 'Two-column hero with image and text side by side', 'Landing',
    ['from-slate-950', 'to-slate-800'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
    <div>
      <span class="inline-block text-blue-400 text-sm font-semibold uppercase tracking-widest mb-4">The smarter way</span>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">Build faster. Ship with confidence.</h1>
      <p class="text-slate-400 text-lg mb-8 leading-relaxed">Everything your team needs to go from idea to production — without the overhead.</p>
      <div class="flex gap-3 flex-wrap">
        <a href="#" class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors">Start building</a>
        <a href="#" class="px-6 py-3 text-slate-300 hover:text-white font-medium transition-colors flex items-center gap-2">Learn more <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg></a>
      </div>
    </div>
    <div class="relative">
      <div class="aspect-[4/3] rounded-2xl bg-gradient-to-br from-blue-600/20 to-purple-700/20 border border-white/10 flex items-center justify-center">
        <span class="text-slate-600 text-sm">Your image here</span>
      </div>
      <div class="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-600/20 rounded-2xl blur-xl"></div>
    </div>
  </div>
</section>`),

  buildTemplate('cta', 'Call to Action', 'Centered CTA block with primary and secondary buttons', 'Landing',
    ['from-blue-700', 'to-purple-700'],
    `<section class="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-700 relative overflow-hidden">
  <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOFYwYzkuOTQgMCAxOCA4LjA2IDE4IDE4aC0xOHoiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L2c+PC9zdmc+')] opacity-20"></div>
  <div class="max-w-3xl mx-auto text-center relative">
    <h2 class="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
    <p class="text-xl text-blue-100 mb-10">Join thousands of teams already building better products, faster.</p>
    <div class="flex gap-4 justify-center flex-wrap">
      <a href="#" class="px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg">Start Free Trial</a>
      <a href="#" class="px-8 py-4 border border-white/40 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors">Schedule Demo</a>
    </div>
    <p class="text-blue-200 text-sm mt-6">No credit card required · Cancel anytime</p>
  </div>
</section>`),

  buildTemplate('banner', 'Announcement Banner', 'Full-width announcement bar for promotions', 'Landing',
    ['from-amber-600', 'to-orange-600'],
    `<section class="py-4 px-6 bg-gradient-to-r from-amber-500 to-orange-500">
  <div class="max-w-6xl mx-auto flex items-center justify-center gap-4 flex-wrap text-center">
    <span class="text-white font-semibold">🎉 Limited time offer — 30% off all plans this week</span>
    <a href="#" class="inline-flex items-center gap-1.5 px-4 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-full transition-colors border border-white/30">
      Claim now <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
    </a>
  </div>
</section>`),

  // ── Content ───────────────────────────────────────────────────────────────
  buildTemplate('features', 'Features', '3-column feature cards with icons and descriptions', 'Content',
    ['from-slate-950', 'to-slate-900'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-6xl mx-auto">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Us</h2>
      <p class="text-slate-400 max-w-xl mx-auto">Everything you need to succeed, all in one place. Built for teams that move fast.</p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div class="p-8 rounded-2xl bg-slate-900 border border-slate-800 hover:border-blue-500/30 transition-colors group">
        <div class="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center mb-5 group-hover:bg-blue-600/30 transition-colors">
          <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">Lightning Fast</h3>
        <p class="text-slate-400 leading-relaxed">Optimized for speed and performance at every level of your workflow.</p>
      </div>
      <div class="p-8 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-500/30 transition-colors group">
        <div class="w-12 h-12 rounded-xl bg-purple-600/20 flex items-center justify-center mb-5 group-hover:bg-purple-600/30 transition-colors">
          <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">Secure by Default</h3>
        <p class="text-slate-400 leading-relaxed">Enterprise-grade security built in, so you can focus on building.</p>
      </div>
      <div class="p-8 rounded-2xl bg-slate-900 border border-slate-800 hover:border-green-500/30 transition-colors group">
        <div class="w-12 h-12 rounded-xl bg-green-600/20 flex items-center justify-center mb-5 group-hover:bg-green-600/30 transition-colors">
          <svg class="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
        </div>
        <h3 class="text-lg font-semibold text-white mb-2">Easy to Use</h3>
        <p class="text-slate-400 leading-relaxed">Intuitive interface designed for teams of all sizes and skill levels.</p>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('how_it_works', 'How It Works', 'Numbered step-by-step process section', 'Content',
    ['from-slate-900', 'to-slate-950'],
    `<section class="py-20 px-6 bg-slate-900">
  <div class="max-w-4xl mx-auto">
    <h2 class="text-3xl font-bold text-white text-center mb-4">How It Works</h2>
    <p class="text-slate-400 text-center mb-16">Get up and running in three simple steps.</p>
    <div class="relative">
      <div class="absolute left-8 top-8 bottom-8 w-px bg-gradient-to-b from-blue-600 via-purple-600 to-green-600 hidden md:block"></div>
      <div class="space-y-12">
        <div class="flex gap-6 items-start">
          <div class="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg shrink-0">1</div>
          <div class="pt-3">
            <h3 class="text-xl font-semibold text-white mb-2">Sign Up in Seconds</h3>
            <p class="text-slate-400 leading-relaxed">Create your account with just an email. No credit card required. You're live in under a minute.</p>
          </div>
        </div>
        <div class="flex gap-6 items-start">
          <div class="w-16 h-16 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-lg shrink-0">2</div>
          <div class="pt-3">
            <h3 class="text-xl font-semibold text-white mb-2">Configure Your Setup</h3>
            <p class="text-slate-400 leading-relaxed">Connect your tools, import your data, and customize settings to match your workflow.</p>
          </div>
        </div>
        <div class="flex gap-6 items-start">
          <div class="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg shrink-0">3</div>
          <div class="pt-3">
            <h3 class="text-xl font-semibold text-white mb-2">Launch with Confidence</h3>
            <p class="text-slate-400 leading-relaxed">Ship your product, reach your audience, and track results — all from one dashboard.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('faq', 'FAQ', 'Accordion-style frequently asked questions', 'Content',
    ['from-slate-950', 'to-slate-950'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-3xl mx-auto">
    <h2 class="text-3xl font-bold text-white text-center mb-4">Frequently Asked Questions</h2>
    <p class="text-slate-400 text-center mb-14">Can't find the answer? <a href="#" class="text-blue-400 hover:underline">Talk to our team</a>.</p>
    <div class="space-y-3" x-data="{ open: null }">
      <div class="rounded-xl border border-slate-800 overflow-hidden">
        <button class="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-900/50 transition-colors" @click="open = open === 0 ? null : 0">
          <span class="text-white font-medium">How do I get started?</span>
          <svg class="w-5 h-5 text-slate-400 transition-transform shrink-0" :class="open === 0 ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div x-show="open === 0" x-collapse class="px-6 pb-5 text-slate-400 leading-relaxed">Sign up for a free account, choose your plan, and you can be up and running in minutes. No setup fees, no hidden charges.</div>
      </div>
      <div class="rounded-xl border border-slate-800 overflow-hidden">
        <button class="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-900/50 transition-colors" @click="open = open === 1 ? null : 1">
          <span class="text-white font-medium">Can I change my plan later?</span>
          <svg class="w-5 h-5 text-slate-400 transition-transform shrink-0" :class="open === 1 ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div x-show="open === 1" x-collapse class="px-6 pb-5 text-slate-400 leading-relaxed">Yes, upgrade or downgrade at any time. Changes are immediate and billing is prorated.</div>
      </div>
      <div class="rounded-xl border border-slate-800 overflow-hidden">
        <button class="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-900/50 transition-colors" @click="open = open === 2 ? null : 2">
          <span class="text-white font-medium">Is there a free trial?</span>
          <svg class="w-5 h-5 text-slate-400 transition-transform shrink-0" :class="open === 2 ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div x-show="open === 2" x-collapse class="px-6 pb-5 text-slate-400 leading-relaxed">All plans include a 14-day free trial. No credit card required to start.</div>
      </div>
      <div class="rounded-xl border border-slate-800 overflow-hidden">
        <button class="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-900/50 transition-colors" @click="open = open === 3 ? null : 3">
          <span class="text-white font-medium">What kind of support do you offer?</span>
          <svg class="w-5 h-5 text-slate-400 transition-transform shrink-0" :class="open === 3 ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div x-show="open === 3" x-collapse class="px-6 pb-5 text-slate-400 leading-relaxed">Email support on all plans. Priority support on Pro. Dedicated account manager for Enterprise.</div>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('stats', 'Stats / Numbers', 'Key metrics displayed with CountUp animation', 'Content',
    ['from-slate-900', 'to-slate-900'],
    `<section class="py-16 px-6 bg-slate-900 border-y border-slate-800">
  <div class="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
    <div>
      <div class="text-4xl font-bold text-white mb-1" data-countup data-target="10000" data-suffix="+" data-duration="2">10K+</div>
      <div class="text-slate-400 text-sm">Happy Customers</div>
    </div>
    <div>
      <div class="text-4xl font-bold text-white mb-1" data-countup data-target="99.9" data-suffix="%" data-duration="2">99.9%</div>
      <div class="text-slate-400 text-sm">Uptime SLA</div>
    </div>
    <div>
      <div class="text-4xl font-bold text-white mb-1" data-countup data-target="4.9" data-suffix="★" data-duration="2">4.9★</div>
      <div class="text-slate-400 text-sm">Average Rating</div>
    </div>
    <div>
      <div class="text-4xl font-bold text-white mb-1" data-countup data-target="2" data-suffix="x" data-duration="2">2x</div>
      <div class="text-slate-400 text-sm">Faster Delivery</div>
    </div>
  </div>
</section>`),

  // ── Commerce ─────────────────────────────────────────────────────────────
  buildTemplate('pricing', 'Pricing', '3-tier pricing cards with feature lists', 'Commerce',
    ['from-slate-950', 'to-blue-950'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-5xl mx-auto">
    <div class="text-center mb-14">
      <h2 class="text-3xl font-bold text-white mb-3">Simple, Transparent Pricing</h2>
      <p class="text-slate-400">Choose the plan that fits your team. No hidden fees.</p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="p-8 rounded-2xl bg-slate-900 border border-slate-800">
        <h3 class="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-4">Starter</h3>
        <div class="text-4xl font-bold text-white mb-1">$9</div>
        <div class="text-slate-500 text-sm mb-8">per month, billed annually</div>
        <ul class="space-y-3 text-slate-400 mb-8">
          <li class="flex items-center gap-2">${CHECK} 5 projects</li>
          <li class="flex items-center gap-2">${CHECK} Basic analytics</li>
          <li class="flex items-center gap-2">${CHECK} Email support</li>
        </ul>
        <a href="#" class="block text-center py-3 rounded-xl border border-slate-700 text-white hover:bg-slate-800 transition-colors font-medium">Get Started</a>
      </div>
      <div class="p-8 rounded-2xl bg-blue-600 border border-blue-500 relative scale-[1.02]">
        <span class="absolute -top-3 left-1/2 -translate-x-1/2 text-xs bg-white text-blue-600 font-bold px-3 py-1 rounded-full whitespace-nowrap">MOST POPULAR</span>
        <h3 class="text-sm font-semibold text-blue-200 uppercase tracking-widest mb-4">Pro</h3>
        <div class="text-4xl font-bold text-white mb-1">$29</div>
        <div class="text-blue-200 text-sm mb-8">per month, billed annually</div>
        <ul class="space-y-3 text-blue-100 mb-8">
          <li class="flex items-center gap-2"><svg class="w-4 h-4 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>Unlimited projects</li>
          <li class="flex items-center gap-2"><svg class="w-4 h-4 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>Advanced analytics</li>
          <li class="flex items-center gap-2"><svg class="w-4 h-4 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>Priority support</li>
        </ul>
        <a href="#" class="block text-center py-3 rounded-xl bg-white text-blue-600 hover:bg-blue-50 transition-colors font-semibold">Get Started</a>
      </div>
      <div class="p-8 rounded-2xl bg-slate-900 border border-slate-800">
        <h3 class="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-4">Enterprise</h3>
        <div class="text-4xl font-bold text-white mb-1">$99</div>
        <div class="text-slate-500 text-sm mb-8">per month, billed annually</div>
        <ul class="space-y-3 text-slate-400 mb-8">
          <li class="flex items-center gap-2">${CHECK} Everything in Pro</li>
          <li class="flex items-center gap-2">${CHECK} Custom integrations</li>
          <li class="flex items-center gap-2">${CHECK} SLA + dedicated manager</li>
        </ul>
        <a href="#" class="block text-center py-3 rounded-xl border border-slate-700 text-white hover:bg-slate-800 transition-colors font-medium">Contact Sales</a>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('products', 'Product Grid', 'E-commerce product cards with image, name, and price', 'Commerce',
    ['from-slate-950', 'to-slate-900'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-6xl mx-auto">
    <div class="flex items-center justify-between mb-10">
      <h2 class="text-2xl font-bold text-white">Featured Products</h2>
      <a href="#" class="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-1">View all <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg></a>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="group">
        <div class="aspect-square rounded-2xl bg-gradient-to-br from-slate-800 to-slate-700 mb-4 flex items-center justify-center border border-slate-700 overflow-hidden group-hover:border-slate-600 transition-colors">
          <span class="text-slate-600 text-xs">Product image</span>
        </div>
        <div class="text-slate-300 text-xs mb-1">Category</div>
        <h3 class="text-white font-medium mb-1">Product Name One</h3>
        <div class="flex items-center justify-between">
          <span class="text-white font-bold">$49.00</span>
          <button class="text-xs px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors">Add to cart</button>
        </div>
      </div>
      <div class="group">
        <div class="aspect-square rounded-2xl bg-gradient-to-br from-purple-900 to-slate-800 mb-4 flex items-center justify-center border border-slate-700 overflow-hidden group-hover:border-slate-600 transition-colors">
          <span class="text-slate-600 text-xs">Product image</span>
        </div>
        <div class="text-slate-300 text-xs mb-1">Category</div>
        <h3 class="text-white font-medium mb-1">Product Name Two</h3>
        <div class="flex items-center justify-between">
          <div><span class="text-white font-bold">$79.00</span><span class="text-slate-500 text-sm line-through ml-2">$99.00</span></div>
          <button class="text-xs px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors">Add to cart</button>
        </div>
      </div>
      <div class="group">
        <div class="aspect-square rounded-2xl bg-gradient-to-br from-blue-950 to-slate-800 mb-4 flex items-center justify-center border border-slate-700 overflow-hidden group-hover:border-slate-600 transition-colors">
          <span class="text-slate-600 text-xs">Product image</span>
        </div>
        <div class="text-slate-300 text-xs mb-1">Category</div>
        <h3 class="text-white font-medium mb-1">Product Name Three</h3>
        <div class="flex items-center justify-between">
          <span class="text-white font-bold">$129.00</span>
          <button class="text-xs px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors">Add to cart</button>
        </div>
      </div>
      <div class="group">
        <div class="aspect-square rounded-2xl bg-gradient-to-br from-green-950 to-slate-800 mb-4 flex items-center justify-center border border-slate-700 overflow-hidden group-hover:border-slate-600 transition-colors">
          <span class="text-slate-600 text-xs">Product image</span>
        </div>
        <div class="text-slate-300 text-xs mb-1">Category</div>
        <h3 class="text-white font-medium mb-1">Product Name Four</h3>
        <div class="flex items-center justify-between">
          <span class="text-white font-bold">$59.00</span>
          <button class="text-xs px-3 py-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors">Add to cart</button>
        </div>
      </div>
    </div>
  </div>
</section>`),

  // ── Social Proof ──────────────────────────────────────────────────────────
  buildTemplate('testimonials', 'Testimonials', 'Customer quotes in a card grid', 'Social Proof',
    ['from-slate-900', 'to-slate-800'],
    `<section class="py-20 px-6 bg-slate-900">
  <div class="max-w-6xl mx-auto">
    <div class="text-center mb-14">
      <h2 class="text-3xl font-bold text-white mb-3">What Our Customers Say</h2>
      <p class="text-slate-400">Trusted by teams at companies of all sizes.</p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="p-8 rounded-2xl bg-slate-800 border border-slate-700">
        ${STARS}
        <p class="text-slate-300 mb-6 leading-relaxed">"This product completely transformed our workflow. We've never been more productive — highly recommended."</p>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">S</div>
          <div><div class="text-white font-medium text-sm">Sarah Chen</div><div class="text-slate-500 text-xs">CTO, TechCorp</div></div>
        </div>
      </div>
      <div class="p-8 rounded-2xl bg-slate-800 border border-slate-700">
        ${STARS}
        <p class="text-slate-300 mb-6 leading-relaxed">"The best investment we've made this year. The ROI was visible within the first month of using it."</p>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">M</div>
          <div><div class="text-white font-medium text-sm">Marcus Johnson</div><div class="text-slate-500 text-xs">Founder, StartupXYZ</div></div>
        </div>
      </div>
      <div class="p-8 rounded-2xl bg-slate-800 border border-slate-700">
        ${STARS}
        <p class="text-slate-300 mb-6 leading-relaxed">"I can't imagine going back to the old way. This has saved our team hundreds of hours every month."</p>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">A</div>
          <div><div class="text-white font-medium text-sm">Aisha Patel</div><div class="text-slate-500 text-xs">VP Product, ScaleUp</div></div>
        </div>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('logos', 'Logo Bar', 'Trusted-by logo row — add your client logos via the Brand panel', 'Social Proof',
    ['from-slate-900', 'to-slate-950'],
    `<section class="py-14 px-6 bg-slate-900 border-y border-slate-800">
  <div class="max-w-5xl mx-auto">
    <p class="text-center text-slate-500 text-sm uppercase tracking-widest mb-8">Trusted by leading companies</p>
    <div class="flex flex-wrap items-center justify-center gap-8 md:gap-12">
      <!-- Replace these SVG logos with your client logos using the Edit with AI tool -->
      <svg class="h-7 opacity-40 hover:opacity-70 transition-opacity" viewBox="0 0 80 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="6" width="12" height="12" rx="3"/><rect x="16" y="0" width="8" height="24" rx="2"/><rect x="28" y="4" width="52" height="6" rx="2"/><rect x="28" y="14" width="36" height="6" rx="2"/>
      </svg>
      <svg class="h-7 opacity-40 hover:opacity-70 transition-opacity" viewBox="0 0 80 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10"/><rect x="26" y="3" width="6" height="18" rx="2"/><rect x="36" y="3" width="44" height="7" rx="2"/><rect x="36" y="14" width="30" height="7" rx="2"/>
      </svg>
      <svg class="h-7 opacity-40 hover:opacity-70 transition-opacity" viewBox="0 0 80 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <polygon points="12,2 22,22 2,22"/><rect x="28" y="4" width="52" height="6" rx="2"/><rect x="28" y="14" width="40" height="6" rx="2"/>
      </svg>
      <svg class="h-7 opacity-40 hover:opacity-70 transition-opacity" viewBox="0 0 80 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="20" height="20" rx="4"/><rect x="4" y="4" width="12" height="12" rx="2" fill="black" opacity="0.5"/><rect x="26" y="4" width="54" height="7" rx="2"/><rect x="26" y="14" width="38" height="6" rx="2"/>
      </svg>
      <svg class="h-7 opacity-40 hover:opacity-70 transition-opacity" viewBox="0 0 80 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L20 12L12 22L4 12Z"/><rect x="28" y="4" width="52" height="6" rx="2"/><rect x="28" y="14" width="44" height="6" rx="2"/>
      </svg>
      <svg class="h-7 opacity-40 hover:opacity-70 transition-opacity" viewBox="0 0 80 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="24" height="8" rx="2"/><rect x="0" y="10" width="24" height="4" rx="2"/><rect x="0" y="16" width="24" height="8" rx="2"/><rect x="30" y="4" width="50" height="6" rx="2"/><rect x="30" y="14" width="34" height="6" rx="2"/>
      </svg>
    </div>
    <p class="text-center text-slate-600 text-xs mt-8">Tip: Click any logo above and use "Edit with AI" to replace it, or upload your client logos via the Brand panel</p>
  </div>
</section>`),

  buildTemplate('rating_bar', 'Star Rating Bar', 'Social proof row with rating badges and counts', 'Social Proof',
    ['from-slate-900', 'to-slate-900'],
    `<section class="py-12 px-6 bg-slate-900 border-y border-slate-800">
  <div class="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-8 text-center">
    <div class="flex flex-col items-center gap-1">
      <div class="flex gap-0.5">${Array(5).fill('<svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>').join('')}</div>
      <div class="text-2xl font-bold text-white">4.9/5</div>
      <div class="text-slate-400 text-sm">Product Hunt</div>
    </div>
    <div class="w-px h-16 bg-slate-700 hidden md:block"></div>
    <div class="flex flex-col items-center gap-1">
      <div class="flex gap-0.5">${Array(5).fill('<svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>').join('')}</div>
      <div class="text-2xl font-bold text-white">4.8/5</div>
      <div class="text-slate-400 text-sm">G2 Crowd</div>
    </div>
    <div class="w-px h-16 bg-slate-700 hidden md:block"></div>
    <div class="flex flex-col items-center gap-1">
      <div class="flex gap-0.5">${Array(5).fill('<svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>').join('')}</div>
      <div class="text-2xl font-bold text-white">1,200+</div>
      <div class="text-slate-400 text-sm">5-Star Reviews</div>
    </div>
  </div>
</section>`),

  // ── About ────────────────────────────────────────────────────────────────
  buildTemplate('team', 'Team', 'Team member cards with photos and bios', 'About',
    ['from-slate-950', 'to-slate-900'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-6xl mx-auto">
    <div class="text-center mb-14">
      <h2 class="text-3xl font-bold text-white mb-3">Meet the Team</h2>
      <p class="text-slate-400">The talented people behind the product.</p>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="text-center group">
        <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 group-hover:scale-105 transition-transform">J</div>
        <div class="text-white font-semibold mb-0.5">Jamie Rivera</div>
        <div class="text-blue-400 text-sm mb-2">Co-Founder & CEO</div>
        <p class="text-slate-500 text-xs leading-relaxed">10+ years building B2B software products.</p>
      </div>
      <div class="text-center group">
        <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 group-hover:scale-105 transition-transform">R</div>
        <div class="text-white font-semibold mb-0.5">Robin Park</div>
        <div class="text-blue-400 text-sm mb-2">CTO</div>
        <p class="text-slate-500 text-xs leading-relaxed">Former principal engineer at Stripe.</p>
      </div>
      <div class="text-center group">
        <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 group-hover:scale-105 transition-transform">L</div>
        <div class="text-white font-semibold mb-0.5">Luna Kim</div>
        <div class="text-blue-400 text-sm mb-2">Head of Design</div>
        <p class="text-slate-500 text-xs leading-relaxed">Led design at Figma and Notion.</p>
      </div>
      <div class="text-center group">
        <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 group-hover:scale-105 transition-transform">T</div>
        <div class="text-white font-semibold mb-0.5">Taylor Osei</div>
        <div class="text-blue-400 text-sm mb-2">Head of Growth</div>
        <p class="text-slate-500 text-xs leading-relaxed">Grew 3 startups to Series B.</p>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('about', 'About Story', 'Company story with mission and values', 'About',
    ['from-slate-900', 'to-slate-950'],
    `<section class="py-20 px-6 bg-slate-900">
  <div class="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
    <div>
      <span class="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-4 block">Our Story</span>
      <h2 class="text-3xl font-bold text-white mb-6">Built by builders,<br>for builders</h2>
      <p class="text-slate-400 leading-relaxed mb-6">We started this company because we were frustrated with the tools available. Everything was either too complex, too expensive, or too slow. So we built what we wished existed.</p>
      <p class="text-slate-400 leading-relaxed mb-8">Today, thousands of teams use our product to ship faster and smarter — without sacrificing quality or reliability.</p>
      <a href="#" class="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors">Read our full story <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg></a>
    </div>
    <div class="grid grid-cols-2 gap-4">
      <div class="p-6 rounded-2xl bg-slate-800 border border-slate-700">
        <div class="text-3xl font-bold text-white mb-1">2019</div>
        <div class="text-slate-400 text-sm">Founded</div>
      </div>
      <div class="p-6 rounded-2xl bg-blue-600/10 border border-blue-600/20">
        <div class="text-3xl font-bold text-white mb-1">10K+</div>
        <div class="text-slate-400 text-sm">Customers</div>
      </div>
      <div class="p-6 rounded-2xl bg-slate-800 border border-slate-700">
        <div class="text-3xl font-bold text-white mb-1">12</div>
        <div class="text-slate-400 text-sm">Countries</div>
      </div>
      <div class="p-6 rounded-2xl bg-purple-600/10 border border-purple-600/20">
        <div class="text-3xl font-bold text-white mb-1">48</div>
        <div class="text-slate-400 text-sm">Team members</div>
      </div>
    </div>
  </div>
</section>`),

  // ── Forms ────────────────────────────────────────────────────────────────
  buildTemplate('contact', 'Contact Form', 'Contact form with name, email, and message fields', 'Forms',
    ['from-slate-950', 'to-slate-900'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-2xl mx-auto">
    <div class="text-center mb-10">
      <h2 class="text-3xl font-bold text-white mb-3">Get in Touch</h2>
      <p class="text-slate-400">We'd love to hear from you. Send us a message and we'll respond within 24 hours.</p>
    </div>
    <form class="space-y-5" data-orbis-form>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-2">First Name</label>
          <input type="text" name="first_name" placeholder="Jane" class="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors" required>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
          <input type="text" name="last_name" placeholder="Doe" class="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors" required>
        </div>
      </div>
      <div>
        <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
        <input type="email" name="email" placeholder="jane@company.com" class="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors" required>
      </div>
      <div>
        <label class="block text-sm font-medium text-slate-300 mb-2">Message</label>
        <textarea name="message" rows="5" placeholder="Tell us how we can help..." class="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors resize-none" required></textarea>
      </div>
      <button type="submit" class="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2">
        Send Message <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
      </button>
    </form>
  </div>
</section>`),

  buildTemplate('newsletter', 'Newsletter', 'Email signup with headline and subscribe button', 'Forms',
    ['from-slate-900', 'to-slate-900'],
    `<section class="py-16 px-6 bg-slate-900">
  <div class="max-w-xl mx-auto text-center">
    <div class="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center mx-auto mb-5">
      <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
    </div>
    <h2 class="text-2xl font-bold text-white mb-3">Stay in the Loop</h2>
    <p class="text-slate-400 mb-8">Get product updates, tips, and early access to new features — delivered weekly.</p>
    <form class="flex gap-3 flex-wrap justify-center" data-orbis-form>
      <input type="email" name="email" placeholder="Enter your email" class="flex-1 min-w-0 px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm" required>
      <button type="submit" class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm shrink-0">Subscribe</button>
    </form>
    <p class="text-slate-600 text-xs mt-4">No spam, ever. Unsubscribe anytime.</p>
  </div>
</section>`),

  // ── Media ────────────────────────────────────────────────────────────────
  buildTemplate('gallery', 'Gallery', 'Responsive image grid / portfolio showcase', 'Media',
    ['from-slate-950', 'to-slate-900'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-6xl mx-auto">
    <div class="text-center mb-12">
      <h2 class="text-3xl font-bold text-white mb-3">Our Work</h2>
      <p class="text-slate-400">A selection of recent projects and case studies.</p>
    </div>
    <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div class="aspect-video rounded-2xl bg-gradient-to-br from-blue-600/30 to-blue-900/40 border border-blue-600/10 flex items-center justify-center group hover:border-blue-500/30 transition-colors overflow-hidden relative">
        <span class="text-blue-300/40 text-xs group-hover:scale-105 transition-transform">Project 1</span>
      </div>
      <div class="aspect-video rounded-2xl bg-gradient-to-br from-purple-600/30 to-purple-900/40 border border-purple-600/10 flex items-center justify-center group hover:border-purple-500/30 transition-colors overflow-hidden">
        <span class="text-purple-300/40 text-xs">Project 2</span>
      </div>
      <div class="aspect-video rounded-2xl bg-gradient-to-br from-green-600/30 to-green-900/40 border border-green-600/10 flex items-center justify-center group hover:border-green-500/30 transition-colors overflow-hidden row-span-1 md:row-span-2">
        <span class="text-green-300/40 text-xs">Project 3</span>
      </div>
      <div class="aspect-video rounded-2xl bg-gradient-to-br from-amber-600/30 to-amber-900/40 border border-amber-600/10 flex items-center justify-center group hover:border-amber-500/30 transition-colors overflow-hidden">
        <span class="text-amber-300/40 text-xs">Project 4</span>
      </div>
      <div class="aspect-video rounded-2xl bg-gradient-to-br from-pink-600/30 to-pink-900/40 border border-pink-600/10 flex items-center justify-center group hover:border-pink-500/30 transition-colors overflow-hidden">
        <span class="text-pink-300/40 text-xs">Project 5</span>
      </div>
    </div>
  </div>
</section>`),

  buildTemplate('video', 'Video Section', 'Centered video embed with play button overlay', 'Media',
    ['from-slate-950', 'to-slate-900'],
    `<section class="py-20 px-6 bg-slate-950">
  <div class="max-w-4xl mx-auto text-center">
    <h2 class="text-3xl font-bold text-white mb-4">See It in Action</h2>
    <p class="text-slate-400 mb-10">Watch how teams use our product to move faster and ship better software.</p>
    <div class="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-slate-900 group cursor-pointer">
      <!-- Replace src with your video thumbnail URL -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
        <div class="w-20 h-20 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center backdrop-blur-sm group-hover:bg-white/20 transition-all group-hover:scale-105">
          <svg class="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
        </div>
      </div>
      <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
    </div>
    <p class="text-slate-600 text-sm mt-4">3:24 min · No audio required</p>
  </div>
</section>`),
];

const CATEGORIES = ['All', 'Landing', 'Content', 'Commerce', 'Social Proof', 'About', 'Forms', 'Media'];

// Example prompts for AI Generate tab (Elementor-style quick fills)
const EXAMPLE_PROMPTS = [
  'A dark hero section with a bold headline, subheadline, and two CTA buttons',
  'A features section with 6 icon cards on a dark background, 2-column on mobile',
  'A pricing comparison table with 3 tiers, monthly/annual toggle using Alpine.js',
  'A timeline section showing company milestones from founding to today',
  'An integrations section showing logos of 12 tools we connect with',
  'A dark footer with logo, 4 column navigation links, and social icons',
  'A before/after split comparison section with a drag handle',
  'A sticky side-by-side section: left fixed text, right scrolling feature list',
];

// ─── Template Card mini-preview swatch ────────────────────────────────────────
function PreviewSwatch({ preview }) {
  const [from, to] = preview || ['from-slate-900', 'to-slate-800'];
  return (
    <div className={`w-full h-10 rounded-lg bg-gradient-to-br ${from} ${to} mb-3 border border-white/5`} />
  );
}

// ─── Section Picker Modal ──────────────────────────────────────────────────────
export default function SectionPicker({ projectId, currentPageSlug, onInserted, onClose }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [mode, setMode] = useState('template'); // 'template' | 'ai'
  const [aiPrompt, setAiPrompt] = useState('');
  const [insertAfter, setInsertAfter] = useState(-1); // -1 = append at end
  const [loading, setLoading] = useState(false);

  const authHeader = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('orbis_token')}`,
  }), []);

  const filteredTemplates = activeCategory === 'All'
    ? SECTION_TEMPLATES
    : SECTION_TEMPLATES.filter(t => t.category === activeCategory);

  const insertTemplate = useCallback(async (template) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/magic/projects/${projectId}/sections/insert`,
        {
          page_slug: currentPageSlug || 'home',
          insert_after: insertAfter,
          html: template.html,
          section_type: template.type,
        },
        { headers: authHeader() }
      );
      if (res.data?.magic_state) {
        onInserted(res.data.magic_state);
        toast.success(`${template.label} section added`);
        onClose();
      }
    } catch (err) {
      toast.error('Failed to insert section');
    } finally {
      setLoading(false);
    }
  }, [projectId, currentPageSlug, insertAfter, authHeader, onInserted, onClose]);

  const insertAi = useCallback(async () => {
    if (!aiPrompt.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/magic/projects/${projectId}/sections/insert`,
        {
          page_slug: currentPageSlug || 'home',
          insert_after: insertAfter,
          ai_prompt: aiPrompt.trim(),
        },
        { headers: authHeader() }
      );
      if (res.data?.magic_state) {
        onInserted(res.data.magic_state);
        toast.success('Section generated and added');
        onClose();
      }
    } catch (err) {
      toast.error('Failed to generate section');
    } finally {
      setLoading(false);
    }
  }, [projectId, currentPageSlug, insertAfter, aiPrompt, authHeader, onInserted, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-3xl rounded-2xl border flex flex-col overflow-hidden"
        style={{
          background: 'var(--orbis-s1)',
          borderColor: 'rgba(255,255,255,0.1)',
          maxHeight: '88vh',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2">
            <LayoutTemplate className="w-4 h-4 text-[var(--orbis-amber)]" />
            <span className="text-sm font-semibold text-[var(--orbis-t1)]">Add Section</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--orbis-s2)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Position selector */}
        <div className="px-5 pt-3 pb-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-[var(--orbis-t3)]">Insert position:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setInsertAfter(-1)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${insertAfter === -1 ? 'bg-[var(--orbis-amber)]/20 text-[var(--orbis-amber)]' : 'text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'}`}
              >
                End of page
              </button>
              <button
                onClick={() => setInsertAfter(0)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${insertAfter === 0 ? 'bg-[var(--orbis-amber)]/20 text-[var(--orbis-amber)]' : 'text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'}`}
              >
                Beginning
              </button>
            </div>
            <span className="text-[var(--orbis-t4)] text-[10px]">Or: use right-click → Insert After on any section</span>
          </div>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <button
            onClick={() => setMode('template')}
            className={`flex items-center gap-1.5 px-5 py-3 text-xs font-medium border-b-2 transition-colors ${
              mode === 'template'
                ? 'border-[var(--orbis-amber)] text-[var(--orbis-t1)]'
                : 'border-transparent text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'
            }`}
          >
            <LayoutTemplate className="w-3.5 h-3.5" />
            Templates
            <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--orbis-s3)', color: 'var(--orbis-t4)' }}>
              {SECTION_TEMPLATES.length}
            </span>
          </button>
          <button
            onClick={() => setMode('ai')}
            className={`flex items-center gap-1.5 px-5 py-3 text-xs font-medium border-b-2 transition-colors ${
              mode === 'ai'
                ? 'border-[var(--orbis-amber)] text-[var(--orbis-t1)]'
                : 'border-transparent text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            AI Generate
          </button>
        </div>

        {mode === 'template' ? (
          <>
            {/* Category filter */}
            <div className="flex gap-2 px-5 py-3 border-b overflow-x-auto" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              {CATEGORIES.map(cat => {
                const count = cat === 'All' ? SECTION_TEMPLATES.length : SECTION_TEMPLATES.filter(t => t.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium shrink-0 transition-colors flex items-center gap-1.5 ${
                      activeCategory === cat
                        ? 'bg-[var(--orbis-s2)] text-[var(--orbis-t1)]'
                        : 'text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'
                    }`}
                  >
                    {cat}
                    <span className="text-[9px] opacity-50">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Template grid */}
            <div className="flex-1 overflow-y-auto p-5">
              <div className="grid grid-cols-2 gap-3">
                {filteredTemplates.map(template => (
                  <button
                    key={template.type}
                    onClick={() => insertTemplate(template)}
                    disabled={loading}
                    className="text-left p-4 rounded-xl border transition-all group hover:border-[var(--orbis-amber)]/50 hover:bg-[var(--orbis-s2)] disabled:opacity-50"
                    style={{ background: 'var(--orbis-bg)', borderColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <PreviewSwatch preview={template.preview} />
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="text-sm font-medium text-[var(--orbis-t1)] group-hover:text-[var(--orbis-amber)] transition-colors">
                        {template.label}
                      </div>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-[var(--orbis-s2)] text-[var(--orbis-t4)] shrink-0">
                        {template.category}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--orbis-t4)] leading-relaxed">{template.description}</p>
                    <div className="mt-3 flex items-center gap-1 text-[10px] text-[var(--orbis-t4)] group-hover:text-[var(--orbis-amber)]/70 transition-colors">
                      <Plus className="w-3 h-3" /> Insert
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* AI Generate mode */
          <div className="flex-1 flex flex-col p-5 gap-4 overflow-y-auto">
            <div>
              <p className="text-xs text-[var(--orbis-t2)] mb-1 font-medium">Describe your section</p>
              <p className="text-xs text-[var(--orbis-t4)] mb-3">
                AI generates HTML styled to match your site's design system — colors, fonts, and layout.
              </p>
              <textarea
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                placeholder="e.g. A dark testimonials section with 4 cards, customer photos, star ratings, and quotes..."
                rows={4}
                className="w-full px-4 py-3 rounded-xl text-sm text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] resize-none focus:outline-none focus:ring-1 focus:ring-[var(--orbis-amber)]/50"
                style={{
                  background: 'var(--orbis-bg)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) insertAi();
                }}
              />
              <p className="text-[10px] text-[var(--orbis-t4)] mt-1.5">⌘↵ to generate · Be as specific as you like</p>
            </div>

            {/* Example prompts */}
            <div>
              <p className="text-[10px] text-[var(--orbis-t4)] uppercase tracking-widest mb-2">Quick examples</p>
              <div className="flex flex-col gap-1.5">
                {EXAMPLE_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setAiPrompt(prompt)}
                    className="text-left text-xs px-3 py-2 rounded-lg transition-colors flex items-center gap-2 group"
                    style={{ background: 'var(--orbis-bg)', border: '1px solid rgba(255,255,255,0.07)', color: 'var(--orbis-t3)' }}
                  >
                    <ArrowRight className="w-3 h-3 shrink-0 opacity-40 group-hover:opacity-80 group-hover:translate-x-0.5 transition-all" />
                    <span className="truncate group-hover:text-[var(--orbis-t1)] transition-colors">{prompt}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={insertAi}
              disabled={loading || !aiPrompt.trim()}
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all disabled:opacity-40 mt-auto"
              style={{ background: 'var(--orbis-amber)', color: '#09090B' }}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating section…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate Section</>
              )}
            </button>
          </div>
        )}

        {loading && (
          <div className="px-5 py-3 border-t flex items-center gap-2 text-xs text-[var(--orbis-t3)]" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {mode === 'ai' ? 'Generating with AI — this takes 15–30s…' : 'Inserting section…'}
          </div>
        )}
      </div>
    </div>
  );
}
